import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { X, Upload, Star, ArrowLeft, ArrowRight, ImagePlus, Loader2, Video, Trash2 } from 'lucide-react';
import { CollectionPicker } from './CollectionPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: turns a stored relative URL into an absolute one for display
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const GarmentModal = ({ isOpen, onClose, onSave, garment, categories, defaultGender = 'MUJER' }) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: defaultGender,
    category: '',
    description: '',
    short_description: '',
    colors: '',
    sizes: '',
    materials: '',
    price: '',
    sale_price: '',
    images: [],
    video_url: '',
    video_poster_url: '',
    inspirational_phrase: '',
    collection: '',
    featured: false
  });

  useEffect(() => {
    if (garment) {
      // Merge images array with legacy image_url for backward compat
      const existing = Array.isArray(garment.images) && garment.images.length > 0
        ? garment.images
        : (garment.image_url ? [garment.image_url] : []);
      setFormData({
        name: garment.name || '',
        gender: garment.gender || defaultGender,
        category: garment.category || '',
        description: garment.description || '',
        short_description: garment.short_description || '',
        colors: garment.colors ? garment.colors.join(', ') : '',
        sizes: garment.sizes ? garment.sizes.join(', ') : '',
        materials: garment.materials || '',
        price: garment.price || '',
        sale_price: garment.sale_price || '',
        images: existing,
        video_url: garment.video_url || '',
        video_poster_url: garment.video_poster_url || '',
        inspirational_phrase: garment.inspirational_phrase || '',
        collection: garment.collection || '',
        featured: garment.featured || false
      });
    } else {
      setFormData({
        name: '',
        gender: defaultGender,
        category: '',
        description: '',
        short_description: '',
        colors: '',
        sizes: defaultGender === 'ACCESORIOS' ? 'Única' : (defaultGender === 'HOMBRE' ? 'S, M, L, XL, XXL' : 'XS, S, M, L'),
        materials: '',
        price: '',
        sale_price: '',
        images: [],
        video_url: '',
        video_poster_url: '',
        inspirational_phrase: '',
        collection: '',
        featured: false
      });
    }
  }, [garment, defaultGender]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fd = new FormData();
        fd.append('file', file);
        try {
          const resp = await axios.post(`${API}/garments/upload-image`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000, // 2 min para fotos grandes / HEIC
          });
          if (resp.data?.url) uploadedUrls.push(resp.data.url);
        } catch (err) {
          console.error('Upload failed for', file.name, err);
          const detail = err.response?.data?.detail
            || (err.code === 'ECONNABORTED' ? 'La subida tardó demasiado. Intenta con una foto más pequeña.' : null)
            || err.message
            || 'Error desconocido';
          toast({
            title: `Error subiendo ${file.name}`,
            description: detail,
            variant: 'destructive'
          });
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
        toast({
          title: '¡Imágenes subidas!',
          description: `${uploadedUrls.length} de ${files.length} imagen(es) añadidas. Recuerda pulsar "Actualizar" para guardar.`
        });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (idx) => {
    const url = formData.images[idx];
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

    // Best-effort delete from disk (only for our uploaded files)
    if (url && url.startsWith('/api/uploads/garments/')) {
      try {
        await axios.delete(`${API}/garments/upload-image`, { params: { url } });
      } catch (err) {
        console.error('Could not delete file from disk', err);
      }
    }
  };

  const handleVideoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/garments/upload-video`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min para vídeos
      });
      if (resp.data && resp.data.url) {
        setFormData(prev => ({
          ...prev,
          video_url: resp.data.url,
          video_poster_url: resp.data.poster_url || ''
        }));
        toast({
          title: '¡Vídeo subido!',
          description: 'Recuerda pulsar "Guardar cambios".'
        });
      } else {
        toast({
          title: 'Respuesta inesperada del servidor',
          description: 'El vídeo subió pero no se obtuvo URL. Recarga y vuelve a intentar.',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Video upload failed', err);
      // Safely extract error message (detail can be string, array or object)
      let detail = 'Error desconocido';
      const respData = err.response && err.response.data;
      if (respData && respData.detail) {
        if (typeof respData.detail === 'string') {
          detail = respData.detail;
        } else if (Array.isArray(respData.detail)) {
          detail = respData.detail.map(d => d.msg || JSON.stringify(d)).join('; ');
        } else {
          detail = JSON.stringify(respData.detail);
        }
      } else if (err.code === 'ECONNABORTED') {
        detail = 'La subida tardó demasiado (>5 min). Prueba con un vídeo más corto o comprímelo antes.';
      } else if (err.response) {
        detail = `Servidor respondió ${err.response.status}: ${err.response.statusText || 'error'}`;
      } else if (err.request) {
        detail = 'No se recibió respuesta del servidor. Comprueba tu conexión.';
      } else if (err.message) {
        detail = err.message;
      }
      toast({
        title: 'Error subiendo vídeo',
        description: detail,
        variant: 'destructive'
      });
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleRemoveVideo = () => {
    setFormData(prev => ({ ...prev, video_url: '', video_poster_url: '' }));
  };

  const moveImage = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= formData.images.length) return;
    const newImages = [...formData.images];
    [newImages[idx], newImages[newIdx]] = [newImages[newIdx], newImages[idx]];
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const setAsMain = (idx) => {
    if (idx === 0) return;
    const newImages = [...formData.images];
    const [main] = newImages.splice(idx, 1);
    newImages.unshift(main);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.gender || !formData.category || !formData.description) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos requeridos',
        variant: 'destructive'
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        colors: formData.colors ? formData.colors.split(',').map(c => c.trim()).filter(c => c) : [],
        sizes: formData.sizes ? formData.sizes.split(',').map(s => s.trim()).filter(s => s) : [],
        price: formData.price ? parseFloat(formData.price) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        images: formData.images,
        video_url: formData.video_url || null,
        video_poster_url: formData.video_poster_url || null,
        // Keep image_url synced with first image for backward compat
        image_url: formData.images[0] || undefined
      };

      if (garment?.id) {
        await axios.put(`${API}/garments/${garment.id}`, payload);
        toast({ title: 'Éxito', description: 'Prenda actualizada correctamente' });
      } else {
        await axios.post(`${API}/garments`, payload);
        toast({ title: 'Éxito', description: 'Prenda creada correctamente' });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving garment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la prenda',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="modal-title">
            {garment ? 'Editar Prenda' : 'Nueva Prenda'}
          </DialogTitle>
          <DialogDescription>
            {garment ? 'Actualiza los detalles de la prenda' : 'Agrega una nueva prenda a la colección'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Gallery Manager */}
          <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4 bg-neutral-50/30">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <ImagePlus className="w-5 h-5 text-black" />
                Galería de imágenes ({formData.images.length})
              </Label>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-images-btn"
                className="border-neutral-300 hover:bg-neutral-100"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Subir imágenes</>
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
                data-testid="file-input-images"
              />
            </div>

            {formData.images.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">
                Aún no hay imágenes. Haz clic en "Subir imágenes" para añadir fotos al producto.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {formData.images.map((url, idx) => (
                  <div
                    key={url + idx}
                    className={`relative group rounded-lg overflow-hidden border-2 ${idx === 0 ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-gray-200'}`}
                    data-testid={`image-thumb-${idx}`}
                  >
                    <img
                      src={resolveImageUrl(url)}
                      alt={`Imagen ${idx + 1}`}
                      className="w-full h-24 object-cover"
                    />
                    {idx === 0 && (
                      <div className="absolute top-1 left-1 bg-yellow-400 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Star className="w-3 h-3" fill="currentColor" />
                        Principal
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex gap-1">
                        {idx !== 0 && (
                          <button
                            type="button"
                            onClick={() => setAsMain(idx)}
                            className="bg-yellow-400 hover:bg-yellow-500 text-white p-1 rounded"
                            title="Marcar como principal"
                            data-testid={`set-main-${idx}`}
                          >
                            <Star className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => moveImage(idx, -1)}
                          disabled={idx === 0}
                          className="bg-white/90 hover:bg-white text-gray-700 p-1 rounded disabled:opacity-40"
                          title="Mover izquierda"
                        >
                          <ArrowLeft className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 1)}
                          disabled={idx === formData.images.length - 1}
                          className="bg-white/90 hover:bg-white text-gray-700 p-1 rounded disabled:opacity-40"
                          title="Mover derecha"
                        >
                          <ArrowRight className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="bg-red-500 hover:bg-red-600 text-white p-1 rounded"
                          title="Eliminar"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              La primera imagen es la principal. Pasa el ratón sobre cada imagen para reordenar o eliminar.
            </p>
          </div>

          {/* Video (opcional) */}
          <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4 bg-neutral-50/30">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Video className="w-5 h-5 text-black" />
                Vídeo del producto (opcional)
              </Label>
              {!formData.video_url && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                    data-testid="upload-video-btn"
                    className="border-neutral-300 hover:bg-neutral-100"
                  >
                    {uploadingVideo ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo vídeo...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Subir vídeo</>
                    )}
                  </Button>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
                    onChange={handleVideoSelected}
                    className="hidden"
                    data-testid="file-input-video"
                  />
                </>
              )}
            </div>

            {!formData.video_url ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Sin vídeo. Formatos MP4/MOV/WEBM. Máx 200 MB.
              </p>
            ) : (
              <div className="flex items-center gap-3">
                <video
                  src={formData.video_url.startsWith('http') ? formData.video_url : `${BACKEND_URL}${formData.video_url}`}
                  className="w-32 h-32 object-cover rounded bg-black"
                  muted
                  playsInline
                  autoPlay
                  loop
                  data-testid="video-preview"
                />
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-2 break-all">{formData.video_url}</p>
                  <button
                    type="button"
                    onClick={handleRemoveVideo}
                    className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-xs font-semibold"
                    data-testid="remove-video-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Quitar vídeo
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ej: Legging Bloom"
              required
              data-testid="input-name"
            />
          </div>

          {/* Gender & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gender">Género *</Label>
              <Select value={formData.gender} onValueChange={(value) => handleChange('gender', value)}>
                <SelectTrigger data-testid="select-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MUJER">Mujer</SelectItem>
                  <SelectItem value="HOMBRE">Hombre</SelectItem>
                  <SelectItem value="ACCESORIOS">Accesorios</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">Categoría *</Label>
              <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(new Set([
                    ...(categories || []),
                    'Leggings', 'Shorts', 'Tops', 'Camisetas', 'Sudaderas',
                    'Chaquetas', 'Pantalones', 'Conjuntos', 'Abrigos',
                    'Enterizos', 'Gorras', 'Bolsas', 'Calcetines', 'Accesorios'
                  ])).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sizes */}
          <div>
            <Label htmlFor="sizes">Tallas Disponibles</Label>
            <Input
              id="sizes"
              value={formData.sizes}
              onChange={(e) => handleChange('sizes', e.target.value)}
              placeholder="Separadas por comas: XS, S, M, L"
              data-testid="input-sizes"
            />
            <p className="text-xs text-gray-500 mt-1">Separa las tallas con comas</p>
          </div>

          {/* Short Description */}
          <div>
            <Label htmlFor="short_description">Descripción Corta</Label>
            <Input
              id="short_description"
              value={formData.short_description}
              onChange={(e) => handleChange('short_description', e.target.value)}
              placeholder="Breve descripción para la tarjeta"
              data-testid="input-short-description"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Descripción Completa *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Descripción detallada e inspiracional de la prenda"
              rows={4}
              required
              data-testid="input-description"
            />
          </div>

          {/* Inspirational Phrase */}
          <div>
            <Label htmlFor="inspirational_phrase">Frase Inspiracional</Label>
            <Input
              id="inspirational_phrase"
              value={formData.inspirational_phrase}
              onChange={(e) => handleChange('inspirational_phrase', e.target.value)}
              placeholder='Ej: "Florece con cada movimiento"'
              data-testid="input-phrase"
            />
          </div>

          {/* Colors */}
          <div>
            <Label htmlFor="colors">Colores Disponibles</Label>
            <Input
              id="colors"
              value={formData.colors}
              onChange={(e) => handleChange('colors', e.target.value)}
              placeholder="Separados por comas: Rosa loto, Gris nube, Lavanda"
              data-testid="input-colors"
            />
            <p className="text-xs text-gray-500 mt-1">Separa los colores con comas</p>
          </div>

          {/* Materials */}
          <div>
            <Label htmlFor="materials">Materiales</Label>
            <Input
              id="materials"
              value={formData.materials}
              onChange={(e) => handleChange('materials', e.target.value)}
              placeholder="Ej: Poliéster con elastano, transpirable"
              data-testid="input-materials"
            />
          </div>

          {/* Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="price">Precio (€)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="45.00"
                data-testid="input-price"
              />
            </div>
            <div>
              <Label htmlFor="sale_price" className="text-red-600">Precio rebajado (€) <span className="text-xs text-neutral-500 font-normal">— opcional</span></Label>
              <Input
                id="sale_price"
                type="number"
                step="0.01"
                value={formData.sale_price}
                onChange={(e) => handleChange('sale_price', e.target.value)}
                placeholder="Ej. 29.90"
                data-testid="input-sale-price"
                className="border-red-300 focus:ring-red-500"
              />
              <p className="text-xs text-neutral-500 mt-1">Si lo rellenas, aparecerá en rebajas con el precio original tachado.</p>
            </div>
          </div>

          {/* Collection */}
          <div>
            <Label htmlFor="collection">Colección</Label>
            <CollectionPicker value={formData.collection} onChange={(v) => handleChange('collection', v)} />
          </div>

          {/* Featured */}
          <div className="flex items-center space-x-2">
            <Switch
              id="featured"
              checked={formData.featured}
              onCheckedChange={(checked) => handleChange('featured', checked)}
              data-testid="switch-featured"
            />
            <Label htmlFor="featured">Marcar como destacada</Label>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 border-t border-neutral-200 z-10 flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto rounded-none border-neutral-300 hover:bg-neutral-100 text-xs font-bold uppercase tracking-[0.2em] py-3"
              data-testid="cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="w-full sm:flex-1 bg-black hover:bg-neutral-800 text-white rounded-none text-xs font-bold uppercase tracking-[0.2em] py-3"
              data-testid="save-button"
            >
              {garment ? '💾 Guardar cambios' : '✨ Crear prenda'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GarmentModal;
